import { useMemo, useState } from "react";
import { ArrowLeft, CheckCircle2, RotateCcw, ShieldCheck } from "lucide-react";
import { course } from "../content/course";
import { assessmentQuestions } from "../content/assessment";
import { scoreAssessment, selectAssessmentQuestions } from "../lib/assessment";
import { shuffleQuizQuestion } from "../lib/quiz";

type AssessmentPageProps = {
  seed: number;
  onBack: () => void;
  onSubmit: (score: number, questionIds: string[]) => void;
};

export function AssessmentPage({
  seed,
  onBack,
  onSubmit,
}: AssessmentPageProps) {
  const questions = useMemo(
    () =>
      selectAssessmentQuestions(assessmentQuestions, course.assessmentQuestionCount, seed)
        .map((question) => shuffleQuizQuestion(question, `assessment:${seed}`)),
    [seed],
  );
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted ? scoreAssessment(questions, answers) : 0;
  const passed = score >= course.passPercentage;

  const submit = () => {
    setSubmitted(true);
    const nextScore = scoreAssessment(questions, answers);
    if (nextScore >= course.passPercentage) {
      onSubmit(
        nextScore,
        questions.map((question) => question.id),
      );
    }
  };

  const retry = () => {
    onSubmit(
      score,
      questions.map((question) => question.id),
    );
  };

  return (
    <article className="assessment">
      <header className="assessment-header">
        <span className="assessment-mark">
          <ShieldCheck size={28} />
        </span>
        <p className="eyebrow">Final knowledge check</p>
        <h1>Prove the boundary, not your memory</h1>
        <p>
          Answer all {questions.length} questions. You need{" "}
          {course.passPercentage}% to complete the course. Retries include
          explanations and a fresh question selection.
        </p>
      </header>

      <div className="assessment-questions">
        {questions.map((question, index) => {
          const selected = answers[question.id];
          return (
            <fieldset className="assessment-question" key={question.id}>
              <legend>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {question.prompt}
              </legend>
              <div className="choice-grid">
                {question.choices.map((choice, choiceIndex) => {
                  const correct =
                    submitted && choiceIndex === question.correctIndex;
                  const wrong =
                    submitted &&
                    selected === choiceIndex &&
                    choiceIndex !== question.correctIndex;
                  return (
                    <label
                      className={`choice ${correct ? "correct" : ""} ${
                        wrong ? "incorrect" : ""
                      }`}
                      key={choice}
                    >
                      <input
                        type="radio"
                        name={question.id}
                        checked={selected === choiceIndex}
                        disabled={submitted}
                        onChange={() =>
                          setAnswers((current) => ({
                            ...current,
                            [question.id]: choiceIndex,
                          }))
                        }
                      />
                      <span>{choice}</span>
                      {correct ? <CheckCircle2 size={18} /> : null}
                    </label>
                  );
                })}
              </div>
              {submitted ? (
                <p className="answer-explanation">{question.explanation}</p>
              ) : null}
            </fieldset>
          );
        })}
      </div>

      {submitted ? (
        <section className={`score-card ${passed ? "passed" : "retry"}`} role="status">
          <strong>{score}%</strong>
          <div>
            <h2>{passed ? "Course complete" : "One more pass"}</h2>
            <p>
              {passed
                ? "You demonstrated the shared safety and delivery principles."
                : `Review the explanations and retry. The pass mark is ${course.passPercentage}%.`}
            </p>
          </div>
        </section>
      ) : null}

      <footer className="lesson-footer">
        <button className="secondary-button" type="button" onClick={onBack}>
          <ArrowLeft size={17} /> Back to What exists already
        </button>
        {!submitted ? (
          <button
            className="primary-button"
            type="button"
            disabled={Object.keys(answers).length !== questions.length}
            onClick={submit}
          >
            Submit answers
          </button>
        ) : !passed ? (
          <button className="primary-button" type="button" onClick={retry}>
            <RotateCcw size={17} /> Try a fresh set
          </button>
        ) : null}
      </footer>
    </article>
  );
}
